import { Draggable, Droppable } from '@hello-pangea/dnd';
import cx from 'classnames';
import { IconButton, InfoButton } from 'components/Buttons';
import Constants from 'config/Constants';
import _ from 'lodash';
import { BlockLayoutFromCsv } from 'pages/documents_v2/views/edit_area/blocks/block_types/BlockLayoutFromCsv';
import { LayoutBlockPagination } from 'pages/documents_v2/views/edit_area/blocks/block_types/layout_block/LayoutBlockPagination';
import { AreaContext } from 'pages/documents_v2/views/edit_area/context/AreaContext';
import { EditAreaContext } from 'pages/documents_v2/views/edit_area/context/EditAreaContext';
import {
    filterAvailableBlocks,
    formatData,
    getBlockData,
    getBlocksFromData,
    parseBlockContent,
    parseBlockData,
} from 'pages/global/BlockLayoutHelper';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Table } from 'react-bootstrap';
import { ArrowDownShort, ArrowLeftShort, ArrowRightShort, ArrowUpShort, Plus, Trash } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';

import HelperFunctions from '../../../../../global/HelperFunctions';
import { Block } from '../Block';
import { EmptyTextBlock } from './EmptyTextBlock';

export const MAX_ROWS = 200;
const emptyArray = [];

export function BlockLayoutBlock({
    block,
    index,
    isDragging,
    readOnly,
    restrictions,
    itemsPerPage = 20,
    areaBlocks = [],
}) {
    const blockData = getBlockData(block);

    if (blockData.readOnly === true) {
        return (
            <div className="py-4">
                <BlockLayoutFromCsv block={block} blockData={blockData} />
            </div>
        );
    }

    return (
        <NormalBlockLayout
            block={block}
            index={index}
            isDragging={isDragging}
            readOnly={readOnly}
            restrictions={restrictions}
            itemsPerPage={itemsPerPage}
            areaBlocks={areaBlocks}
        />
    );
}

function NormalBlockLayout({ block, index, isDragging, readOnly, restrictions, itemsPerPage = 20, areaBlocks = [] }) {
    const [itemOffset, setItemOffset] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedSearchBlock, setSelectedSearchBlock] = useState(undefined);

    const blockContentAsString = JSON.stringify(getBlockContent());

    const blockContent = useMemo(() => {
        return JSON.parse(blockContentAsString);
    }, [blockContentAsString]);

    const rows = blockContent?.data ?? emptyArray;
    const pageCount = Math.ceil(rows.length / itemsPerPage);
    const columnsPerRow = rows.length > 0 ? rows[0].length : 2;

    useEffect(() => {
        // When removing rows, move to the previous page if the offset is bigger than the number of rows
        if (itemOffset >= rows.length) {
            setItemOffset(Math.max(itemOffset - itemsPerPage, 0));
        }
    }, [itemOffset, rows, itemsPerPage]);

    const handlePageClick = useCallback(
        (event) => {
            const newOffset = event.selected * itemsPerPage;
            setItemOffset(newOffset);
        },
        [itemsPerPage],
    );

    const currentItems = useMemo(() => {
        const newPageCount = Math.ceil(rows.length / itemsPerPage);
        let tempOffset = itemOffset;

        if (newPageCount < pageCount) {
            tempOffset = 0;
        }

        const endOffset = tempOffset + itemsPerPage;
        return rows.slice(tempOffset, endOffset);
    }, [rows, itemsPerPage, itemOffset, pageCount]);

    const canAddNewRow = rows.length < MAX_ROWS;

    return (
        <>
            {rows.length >= itemsPerPage && (
                <div className="pt-4">
                    <LayoutBlockPagination
                        currentItemsLength={currentItems.length}
                        handlePageClick={handlePageClick}
                        itemOffset={itemOffset}
                        pageCount={pageCount}
                        totalItemsLength={rows.length}
                        itemsPerPage={itemsPerPage}
                        columnsPerRow={columnsPerRow}
                        setItemOffset={setItemOffset}
                        rows={rows}
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                        setSelectedSearchBlock={setSelectedSearchBlock}
                        readOnly={readOnly}
                        areaBlocks={areaBlocks}
                    />
                </div>
            )}

            <BlockLayout
                currentItems={currentItems}
                currentLayoutBlockContent={blockContent}
                block={block}
                index={index}
                isDropDisabled={isDragging}
                readOnly={readOnly}
                canChangeColumnsAndRows={restrictions.canChangeColumnsAndRows}
                setItemOffset={setItemOffset}
                itemsPerPage={itemsPerPage}
                itemOffset={itemOffset}
                searchTerm={searchTerm}
                selectedSearchBlock={selectedSearchBlock}
                areaBlocks={areaBlocks}
                canAddNewRow={canAddNewRow}
            />
        </>
    );

    function getBlockContent() {
        if (readOnly && block.hasOwnProperty('renderedDiffContent')) {
            return HelperFunctions.tryParseJSON(block.renderedDiffContent, {});
        }

        return filterAvailableBlocks(parseBlockContent(block.latestContent || block.baseContent), areaBlocks);
    }
}

function BlockLayout({
    currentItems,
    currentLayoutBlockContent,
    block,
    index,
    isDropDisabled = false,
    readOnly,
    setItemOffset,
    itemsPerPage,
    itemOffset,
    canChangeColumnsAndRows = true,
    searchTerm,
    selectedSearchBlock,
    areaBlocks = [],
    canAddNewRow,
}) {
    const [blockVariants, setBlockVariants] = useState(block.documentVariants);
    const [diffVariants, setDiffVariants] = useState({ added: [], removed: [] });
    const currentPage = Math.ceil((itemOffset + 1) / itemsPerPage);
    const startingIndex = (currentPage - 1) * itemsPerPage;
    const endingIndex = Math.min(startingIndex + itemsPerPage, currentLayoutBlockContent?.data.length);
    const currentPageRowLength = endingIndex - startingIndex;
    const { updateBlock } = useContext(EditAreaContext);

    useEffect(() => {
        const added = _.difference(block.documentVariants, blockVariants);
        const removed = _.difference(blockVariants, block.documentVariants);

        if (added.length > 0 || removed.length > 0) {
            setDiffVariants({
                added,
                removed,
            });
        }

        setBlockVariants(block.documentVariants);
    }, [block.documentVariants]);

    useEffect(() => {
        const { added, removed } = diffVariants;

        if (!readOnly && (added.length > 0 || removed.length > 0)) {
            const data = getBlockData(block);
            const blocks = getBlocksFromData(data, areaBlocks);

            blocks.forEach((_block) => {
                const blockIndex = areaBlocks.findIndex((areaBlock) => areaBlock.key === _block.key);

                if (blockIndex >= 0) {
                    const childBlock = areaBlocks[blockIndex];
                    const layoutType = childBlock.properties.textLayout;

                    if (block.properties.type === 'textExplanation' && layoutType === 'default') {
                        // Mirror variants
                        updateBlock(block.documentVariants, blockIndex, 'documentVariants');
                    } else {
                        let newChildVariants = [...childBlock.documentVariants];

                        if (removed.length > 0) {
                            newChildVariants = _.pullAll(newChildVariants, removed);
                        }

                        if (added.length > 0) {
                            newChildVariants = _.union(newChildVariants, added);
                        }

                        updateBlock(newChildVariants, blockIndex, 'documentVariants');
                    }
                }
            });
        }
    }, [diffVariants]);

    useEffect(() => {
        if (
            ((block.deleted === true && readOnly) || (block.deleted === undefined && !readOnly)) &&
            block.properties.type === 'textExplanation'
        ) {
            const data = getBlockData(block);
            const blocks = getBlocksFromData(data, areaBlocks);

            blocks.forEach((_block) => {
                const blockIndex = areaBlocks.findIndex((areaBlock) => areaBlock.key === _block.key);

                if (blockIndex >= 0) {
                    updateBlock(block.deleted, blockIndex, 'deleted');
                }
            });
        }
    }, [block.deleted]);

    const addRow = useCallback(
        (rowIndex = undefined) => {
            const blockData = currentLayoutBlockContent.data;
            const indexToAdd = rowIndex !== undefined ? rowIndex + itemOffset : blockData.length;

            const columnCount = blockData[0].length;
            blockData.splice(indexToAdd, 0, Array(columnCount).fill([]));

            const newContent = formatData(blockData);

            if (rowIndex === undefined) {
                const newRowCount = blockData.length;
                const lastPage = Math.ceil(newRowCount / itemsPerPage) - 1;
                const newOffset = lastPage * itemsPerPage;
                setItemOffset(newOffset);
            }

            updateBlock(parseBlockData(newContent), index);
        },
        [currentLayoutBlockContent, index, itemOffset, itemsPerPage],
    );

    const addColumn = useCallback(
        (columnIndex) => {
            const newBlockData = currentLayoutBlockContent.data.map((_row) => {
                const newRow = [..._row];
                newRow.splice(columnIndex, 0, []);

                return newRow;
            });
            const newContent = formatData(newBlockData);

            updateBlock(parseBlockData(newContent), index);
        },
        [currentLayoutBlockContent, index],
    );

    const removeRow = useCallback(
        (rowIndex = undefined) => {
            const blockData = currentLayoutBlockContent.data;
            const indexToRemove =
                rowIndex !== undefined ? rowIndex + itemOffset : startingIndex + currentPageRowLength - 1;

            const newContent = formatData(blockData.filter((_item, _index) => _index !== indexToRemove));

            updateBlock(parseBlockData(newContent), index);
        },
        [currentLayoutBlockContent, index, itemOffset],
    );

    const removeColumn = useCallback(
        (columnIndex) => {
            const newBlockData = currentLayoutBlockContent.data.map((_row) => {
                const newRow = [..._row];
                newRow.splice(columnIndex, 1);

                return newRow;
            });
            const newContent = formatData(newBlockData);

            updateBlock(parseBlockData(newContent), index);
        },
        [currentLayoutBlockContent, index],
    );

    return (
        <RenderBlockLayout
            block={block}
            addRow={addRow}
            addColumn={addColumn}
            removeRow={removeRow}
            removeColumn={removeColumn}
            isDropDisabled={isDropDisabled}
            readOnly={readOnly}
            areaBlocks={areaBlocks}
            currentLayoutBlockContent={currentLayoutBlockContent}
            blockLayout={currentItems}
            itemOffset={itemOffset}
            canChangeColumnsAndRows={canChangeColumnsAndRows}
            searchTerm={searchTerm}
            selectedSearchBlock={selectedSearchBlock}
            blockLayoutIndex={index}
            canAddNewRow={canAddNewRow}
        />
    );
}

function RenderBlockLayout({
    block,
    addRow,
    addColumn,
    removeRow,
    removeColumn,
    isDropDisabled,
    readOnly,
    areaBlocks,
    currentLayoutBlockContent,
    blockLayout = [],
    itemOffset,
    canChangeColumnsAndRows = true,
    searchTerm,
    selectedSearchBlock,
    blockLayoutIndex,
    canAddNewRow,
}) {
    const { draggedItemKey } = useContext(AreaContext);
    const { properties } = block;
    const isDragging = !!draggedItemKey;

    const showTableHeader = properties?.showTableHeader ?? true;
    const highlightFirstColumn = properties?.highlightFirstColumn ?? false;
    const fixedLayout = properties?.fixedLayout ?? false;

    const columnWidth =
        blockLayout[0] && blockLayout[0].length > 0 ? Math.round(100 / blockLayout[0].length) + '%' : '100%';

    return (
        <div className="table-responsive">
            <Table bordered className="mb-2">
                <tbody>
                    {blockLayout.map((el, ind) => (
                        <tr key={block.key + '-row-' + ind}>
                            {el.map((col, colIndex) => {
                                const isTableHeader =
                                    (showTableHeader && ind === 0 && itemOffset === 0) ||
                                    (highlightFirstColumn && colIndex === 0);

                                if (readOnly) {
                                    return (
                                        <td
                                            width={columnWidth}
                                            style={getListStyle(false, isTableHeader)}
                                            key={block.key + '-col-' + colIndex}
                                        >
                                            <div>
                                                {col.map((item, index) => (
                                                    <LayoutItem
                                                        index={index}
                                                        blockIndex={index}
                                                        itemId={item.id}
                                                        areaBlock={areaBlocks.find((_block) => _block.key === item.id)}
                                                        areaBlocks={areaBlocks}
                                                        readOnly={true}
                                                        searchTerm={searchTerm}
                                                        selectedSearchBlock={selectedSearchBlock}
                                                        key={`layout-${item.id}-${index}`}
                                                    />
                                                ))}
                                            </div>
                                        </td>
                                    );
                                }

                                return (
                                    <DroppableLayoutItem
                                        block={block}
                                        isDropDisabled={fixedLayout || isDropDisabled}
                                        isDragging={isDragging}
                                        colIndex={colIndex}
                                        columnWidth={columnWidth}
                                        ind={ind}
                                        itemOffset={itemOffset}
                                        showTableHeader={isTableHeader}
                                        currentLayoutBlockContent={currentLayoutBlockContent}
                                        blockLayoutIndex={blockLayoutIndex}
                                        key={block.key + '-col-' + colIndex}
                                    >
                                        {col.map((item, index) => {
                                            const blockKey = parseInt(item.id);
                                            const blockIndex = areaBlocks?.findIndex(
                                                (_block) => _block.key === blockKey,
                                            );
                                            const areaBlock = blockIndex < 0 ? undefined : areaBlocks[blockIndex];

                                            return (
                                                <LayoutItem
                                                    areaBlock={areaBlock}
                                                    parentBlock={block}
                                                    index={index}
                                                    blockIndex={blockIndex}
                                                    fixedLayout={fixedLayout}
                                                    searchTerm={searchTerm}
                                                    selectedSearchBlock={selectedSearchBlock}
                                                    key={`layout-${item.id}`}
                                                />
                                            );
                                        })}
                                    </DroppableLayoutItem>
                                );
                            })}

                            {canChangeColumnsAndRows && !fixedLayout && !readOnly && (
                                <td style={{ minWidth: '45px' }}>
                                    <RowActions
                                        row={el}
                                        index={ind}
                                        removeRow={removeRow}
                                        addRow={addRow}
                                        canAddNewRow={canAddNewRow}
                                    />
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>

                {canChangeColumnsAndRows && !fixedLayout && !readOnly && (
                    <ColumnActions
                        addColumn={addColumn}
                        removeColumn={removeColumn}
                        blockLayout={blockLayout}
                        content={currentLayoutBlockContent}
                    />
                )}
            </Table>
        </div>
    );
}

function DroppableLayoutItem({
    block,
    isDropDisabled,
    ind,
    itemOffset,
    colIndex,
    columnWidth,
    showTableHeader,
    isDragging,
    currentLayoutBlockContent,
    blockLayoutIndex,
    children,
}) {
    const isTextExplanationBlock = block?.properties?.type === Constants.blockTypes.textExplanation;

    return (
        <Droppable
            type="LAYOUT_BLOCK"
            isDropDisabled={isDropDisabled}
            droppableId={`block|${block.key}|${ind + itemOffset}|${colIndex}`}
        >
            {(provided, snapshot) => (
                <td
                    width={columnWidth}
                    ref={provided.innerRef}
                    className={cx('block-layout-droppable-cell position-relative p-3 pt-4', {
                        'pl-4': !isDropDisabled,
                    })}
                    style={getListStyle(snapshot.isDraggingOver, showTableHeader, isDragging)}
                    {...provided.droppableProps}
                >
                    {children}
                    {provided.placeholder}

                    {!isTextExplanationBlock && (
                        <InsertBlockBtn
                            block={block}
                            rowIndex={ind + itemOffset}
                            colIndex={colIndex}
                            currentLayoutBlockContent={currentLayoutBlockContent}
                            index={blockLayoutIndex}
                        />
                    )}
                </td>
            )}
        </Droppable>
    );
}

function LayoutItem({
    areaBlock,
    parentBlock,
    index,
    blockIndex,
    fixedLayout,
    readOnly = false,
    searchTerm,
    selectedSearchBlock,
}) {
    if (!areaBlock) {
        return null;
    }

    const blockContent = areaBlock.latestContent || areaBlock.baseContent || '';

    if (readOnly && _.isEmpty(blockContent)) {
        return <EmptyTextBlock />;
    }

    const isDraggable = !fixedLayout && !readOnly;

    return (
        <Draggable draggableId={`${areaBlock.key}`} index={index} isDragDisabled={!isDraggable}>
            {(provided, snapshot) => (
                <div ref={provided.innerRef} {...provided.draggableProps}>
                    <Block
                        block={areaBlock}
                        parentBlock={parentBlock}
                        index={blockIndex}
                        renderedIndex={index}
                        isDraggable={isDraggable}
                        showSettings={false}
                        isInsideBlockLayout={true}
                        searchTerm={searchTerm}
                        selectedSearchBlock={selectedSearchBlock}
                        readOnly={readOnly}
                        isDragging={snapshot.isDragging}
                        dragHandleProps={provided.dragHandleProps}
                    />
                </div>
            )}
        </Draggable>
    );
}

function RowActions({ row = [], index, removeRow, addRow, canAddNewRow = true }) {
    const hasBlocks = row.some((_item) => _item.length > 0);
    const { t } = useTranslation('documents');

    return (
        <div>
            <IconButton
                disabled={!canAddNewRow}
                tooltip={t('document.navbar.main.editor.left.blocks.types.blockLayout.menu.rowAbove')}
                icon={<ArrowUpShort />}
                onClick={() => addRow(index)}
            />
            <IconButton
                className="mb-2"
                disabled={!canAddNewRow}
                tooltip={t('document.navbar.main.editor.left.blocks.types.blockLayout.menu.rowBelow')}
                icon={<ArrowDownShort />}
                onClick={() => addRow(index + 1)}
            />
            <IconButton
                disabled={hasBlocks}
                tooltip={t('document.navbar.main.editor.left.blocks.types.blockLayout.menu.deleteRow')}
                icon={
                    <Trash
                        className={cx({
                            'text-danger': !hasBlocks,
                            'text-muted': hasBlocks,
                        })}
                    />
                }
                onClick={() => removeRow(index)}
            />
        </div>
    );
}

function ColumnActions({ addColumn, blockLayout, removeColumn, content }) {
    const { t } = useTranslation('documents');

    if (!blockLayout || blockLayout.length === 0) {
        return null;
    }

    return (
        <tfoot>
            <tr>
                {blockLayout[0].map((_col, index) => {
                    const hasBlocks = columnHasBlocks(index);

                    return (
                        <td key={`footer-col-${index}`}>
                            <div className="flex-center justify-content-center">
                                <IconButton
                                    tooltip={t(
                                        'document.navbar.main.editor.left.blocks.types.blockLayout.menu.colLeft',
                                    )}
                                    icon={<ArrowLeftShort />}
                                    onClick={() => addColumn(index)}
                                />
                                <IconButton
                                    tooltip={t(
                                        'document.navbar.main.editor.left.blocks.types.blockLayout.menu.colRight',
                                    )}
                                    icon={<ArrowRightShort />}
                                    onClick={() => addColumn(index + 1)}
                                />
                                <IconButton
                                    className="ml-1"
                                    disabled={hasBlocks}
                                    icon={
                                        <Trash
                                            className={cx({
                                                'text-danger': !hasBlocks,
                                                'text-muted': hasBlocks,
                                            })}
                                        />
                                    }
                                    tooltip={t(
                                        'document.navbar.main.editor.left.blocks.types.blockLayout.menu.deleteCol',
                                    )}
                                    onClick={() => removeColumn(index)}
                                />
                            </div>
                        </td>
                    );
                })}

                <td />
            </tr>
        </tfoot>
    );

    function columnHasBlocks(columnIndex) {
        return content.data && content.data.some((_item) => _item[columnIndex].length > 0);
    }
}

function InsertBlockBtn({ block, currentLayoutBlockContent, rowIndex, colIndex, index }) {
    const { addBlock, updateBlock } = useContext(EditAreaContext);
    const { t } = useTranslation('documents');

    return (
        <InfoButton
            style={{ position: 'absolute', bottom: 2, left: '50%', transform: 'translate(-50%, 0)' }}
            size="sm"
            className="insert-block-btn p-1"
            tooltip={t('document.navbar.main.editor.left.blocks.tooltips.insertBlock')}
            onClick={() => insertBlock()}
        >
            <Plus size={16} className="mr-0" />
        </InfoButton>
    );

    function insertBlock() {
        // Create new Block first
        const newBlock = addBlock(Constants.blockTypes.text, block.documentVariants);

        // Add it to the layout
        const currentData = currentLayoutBlockContent.data;

        // Append to end of array
        currentData[rowIndex][colIndex] = [
            ...currentData[rowIndex][colIndex],
            {
                id: newBlock.key,
                new: true,
            },
        ];

        const newContent = formatData(currentData);
        updateBlock(parseBlockData(newContent), index);
    }
}

export const getListStyle = (isDraggingOver, isTableHeader, isDragging = false) => ({
    background: isDraggingOver ? '#00e67614' : isDragging ? undefined : isTableHeader ? '#f6f8fa' : undefined,
    height: 87,
    minWidth: 140,
    overflow: isDragging ? 'hidden' : undefined,
});
